import { useLocation, Link } from "react-router-dom";
import {
  ArrowLeftRight,
  Wallet,
  Brain,
  TrendingUp,
  Building2,
  Bell,
  Settings,
  LogOut,
  ChevronsUpDown,
  User,
  Sun,
  Moon,
  LayoutGrid,
  Server,
  Monitor,
  BarChart3,
  ShieldAlert,
  Target,
  Image,
} from "@/components/ui/icons";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "motion/react";
import { useTheme } from "next-themes";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useDashboardStore } from "@/store/dashboard.store";
import { useSession, signOut } from "@/lib/auth-client";
import { displayName, initialFor } from "@/lib/identity";
import { useState, useSyncExternalStore, type ReactNode } from "react";

/** Landing login URL (matches ProtectedRoute.bounceToLogin so the dev/prod
 *  split stays in one pattern). Used as the post-signOut destination. */
function landingLoginHref(): string {
  if (typeof window === "undefined") return "https://pfh.trading/login";
  return window.location.hostname === "localhost"
    ? "http://localhost:5173/login"
    : "https://pfh.trading/login";
}

async function handleSignOut(): Promise<void> {
  // Clear the Better Auth session, then hop over to the landing's /login.
  // Done in finally so even a transient network failure during sign-out
  // still redirects the user off the dashboard — they can retry from
  // /login if the cookie somehow survived.
  try {
    await signOut();
  } catch (err) {
    console.error("[auth] signOut failed", err);
  } finally {
    window.location.href = landingLoginHref();
  }
}

const navMain = [
  { title: "Overview", href: "/", icon: LayoutGrid },
];

const navFx = [
  { title: "Accounts", href: "/accounts", icon: Wallet },
  { title: "Pairs", href: "/pairs", icon: ArrowLeftRight },
  { title: "Terminal", href: "/terminal", icon: Monitor },
  { title: "Bots", href: "/bots", icon: Server },
];

const navFutures = [
  { title: "Accounts", href: "/futures/accounts", icon: Wallet },
  { title: "Partner Sets", href: "/futures/partner-sets", icon: ArrowLeftRight },
  { title: "Terminal", href: "/futures/terminal", icon: BarChart3 },
  { title: "Platforms", href: "/futures/platforms", icon: Server },
  { title: "Risk", href: "/futures/risk", icon: ShieldAlert },
  { title: "Bots", href: "/futures/bots", icon: Target },
];

const navIntel = [
  { title: "Decisions", href: "/decisions", icon: Brain },
  { title: "Compound", href: "/compound", icon: TrendingUp },
  { title: "Share Card", href: "/share-card", icon: Image },
  { title: "Matcher", href: "/accounts-matcher", icon: Target },
  { title: "Firms", href: "/firms", icon: Building2 },
];

const navSystem = [
  { title: "Settings", href: "/settings", icon: Settings },
];

function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname.startsWith(href);
}

type UserMenuItem =
  | { icon: typeof User; label: string; href: string; destructive?: boolean }
  | { icon: typeof User; label: string; onSelect: () => void; destructive?: boolean };

const userMenuItems: UserMenuItem[] = [
  { icon: User, label: "Profile", href: "/settings" },
  { icon: Settings, label: "Settings", href: "/settings" },
  { icon: LogOut, label: "Log Out", onSelect: () => void handleSignOut(), destructive: true },
];

function useIsMounted(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );
}

const iconEase = [0.25, 0.46, 0.45, 0.94] as const;

function SidebarNavGroup({
  label,
  items,
  pathname,
}: {
  label: string;
  items: Array<{ title: string; href: string; icon: typeof LayoutGrid }>;
  pathname: string;
}): ReactNode {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.href}>
              <SidebarMenuButton
                render={<Link to={item.href} />}
                isActive={isActive(pathname, item.href)}
                tooltip={item.title}
              >
                <item.icon />
                <span>{item.title}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/** Theme toggle as a SidebarMenuButton — collapses to icon when rail is iconified. */
function ThemeMenuItem(): ReactNode {
  const mounted = useIsMounted();
  const { setTheme, resolvedTheme } = useTheme();
  const isDark = mounted && resolvedTheme === "dark";

  const label = mounted ? (isDark ? "Light Mode" : "Dark Mode") : "Theme";

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setTheme(isDark ? "light" : "dark")}
        tooltip={label}
        aria-label={label}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isDark ? (
            <motion.span
              key="sun"
              initial={{ rotate: -90, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              exit={{ rotate: 90, scale: 0 }}
              transition={{ duration: 0.25, ease: iconEase }}
              className="inline-flex"
            >
              <Sun className="size-4" />
            </motion.span>
          ) : (
            <motion.span
              key="moon"
              initial={{ rotate: 90, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              exit={{ rotate: -90, scale: 0 }}
              transition={{ duration: 0.25, ease: iconEase }}
              className="inline-flex"
            >
              <Moon className="size-4" />
            </motion.span>
          )}
        </AnimatePresence>
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** Alerts button with unread badge — collapses to icon in rail mode. */
function AlertsMenuItem({ pathname }: { pathname: string }): ReactNode {
  const unreadAlertCount = useDashboardStore((s) => s.unreadAlertCount);
  const markAlertsRead = useDashboardStore((s) => s.markAlertsRead);
  const hasUnread = unreadAlertCount > 0;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<Link to="/alerts" />}
        isActive={isActive(pathname, "/alerts")}
        onClick={markAlertsRead}
        tooltip={hasUnread ? `Alerts (${unreadAlertCount} unread)` : "Alerts"}
      >
        <span className="relative inline-flex">
          <Bell className="size-4" />
          {hasUnread && (
            <span className="absolute -right-1.5 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-white">
              {unreadAlertCount > 99 ? "99+" : unreadAlertCount}
            </span>
          )}
        </span>
        <span>Alerts</span>
        {hasUnread && (
          <span className="ml-auto rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-white group-data-[collapsible=icon]:hidden">
            {unreadAlertCount > 99 ? "99+" : unreadAlertCount}
          </span>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function UserDropdown(): ReactNode {
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const user = session?.user;
  const label = displayName(user?.name, user?.email);
  const email = user?.email ?? "";
  const initial = initialFor(user?.name, user?.email);

  return (
    <RadixDropdown.Root open={open} onOpenChange={setOpen}>
      <RadixDropdown.Trigger asChild>
        <SidebarMenuButton
          size="lg"
          tooltip={label}
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <Avatar size="sm">
            <AvatarFallback className="bg-accent/10 text-accent text-[11px] font-semibold">
              {initial}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-semibold">{label}</span>
            <span className="truncate text-xs text-muted-foreground">
              {email}
            </span>
          </div>
          <ChevronsUpDown
            className={`ml-auto size-4 text-muted-foreground transition-transform duration-200 group-data-[collapsible=icon]:hidden ${open ? "rotate-180" : ""}`}
          />
        </SidebarMenuButton>
      </RadixDropdown.Trigger>

      <AnimatePresence>
        {open && (
          <RadixDropdown.Portal forceMount>
            <RadixDropdown.Content
              asChild
              side="top"
              align="start"
              sideOffset={8}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 4 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  transformOrigin:
                    "var(--radix-dropdown-menu-content-transform-origin)",
                }}
                className="z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] rounded-2xl border border-border bg-popover p-2 shadow-lg"
              >
                {/* User info header */}
                <div className="mb-2 flex items-center gap-3 rounded-xl px-3 py-2">
                  <Avatar size="default">
                    <AvatarFallback className="bg-accent/10 text-accent text-sm font-semibold">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-sm leading-tight">
                    <p className="font-semibold text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{email}</p>
                  </div>
                </div>

                <div className="mx-2 mb-2 h-px bg-border" />

                {userMenuItems.map((item) => {
                  const className = `flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm outline-none select-none transition-colors duration-100 hover:bg-accent hover:text-white focus:bg-accent focus:text-white ${
                    item.destructive
                      ? "text-destructive hover:bg-destructive hover:text-white focus:bg-destructive"
                      : "text-foreground"
                  }`;
                  if ("onSelect" in item) {
                    return (
                      <RadixDropdown.Item
                        key={item.label}
                        onSelect={item.onSelect}
                        className={className}
                      >
                        <item.icon className="size-4 shrink-0" />
                        {item.label}
                      </RadixDropdown.Item>
                    );
                  }
                  return (
                    <RadixDropdown.Item key={item.label} asChild>
                      <Link to={item.href} className={className}>
                        <item.icon className="size-4 shrink-0" />
                        {item.label}
                      </Link>
                    </RadixDropdown.Item>
                  );
                })}
              </motion.div>
            </RadixDropdown.Content>
          </RadixDropdown.Portal>
        )}
      </AnimatePresence>
    </RadixDropdown.Root>
  );
}

export function AppSidebar(): ReactNode {
  const { pathname } = useLocation();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<Link to="/" />} tooltip="PropFirm Hunter">
              <img
                src="/apple-icon.png"
                alt="PFH"
                className="size-8 rounded-lg object-contain drop-shadow-[0_0_6px_rgba(200,50,100,0.35)]"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">PropFirm Hunter</span>
                <span className="truncate text-xs text-muted-foreground">Dashboard</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarNavGroup label="Main" items={navMain} pathname={pathname} />
        <SidebarNavGroup label="FX & CFD" items={navFx} pathname={pathname} />
        <SidebarNavGroup label="Futures" items={navFutures} pathname={pathname} />
        <SidebarNavGroup label="Intel" items={navIntel} pathname={pathname} />

        {/* System nav */}
        <SidebarGroup>
          <SidebarGroupLabel>System</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navSystem.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    render={<Link to={item.href} />}
                    isActive={isActive(pathname, item.href)}
                    tooltip={item.title}
                  >
                    <item.icon />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <AlertsMenuItem pathname={pathname} />
          <ThemeMenuItem />
          <SidebarMenuItem>
            <UserDropdown />
          </SidebarMenuItem>
        </SidebarMenu>
        <div
          className="px-2 pb-1 pt-0.5 font-mono text-[10px] tracking-wider text-muted-foreground/70 group-data-[collapsible=icon]:hidden"
          title="Dashboard build (sha · utc)"
        >
          v{__APP_VERSION__}
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
